import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

// Use exact same types as useFamily.tsx to maintain compatibility
export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  date_of_birth?: string;
  family_role?: string;
}

export interface Family {
  id: string;
  name: string;
  created_by: string;
}

export interface FamilyMember {
  id: string;
  role: 'father' | 'mother' | 'son' | 'daughter' | 'grandfather' | 'grandmother' | 'guardian_male' | 'guardian_female' | 'parent' | 'spouse' | 'child' | 'grandparent' | 'guardian';
  is_creator: boolean;
  child_name?: string;
  child_birth_date?: string;
  child_gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  child_avatar_url?: string;
  profile_id?: string;
  profiles?: Profile;
  family_id: string;
  created_at: string;
  updated_at: string;
}

const CACHE_TIME = 8 * 60 * 1000; // 8 minutes
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

export function useOptimizedFamily() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Helper function to get the best family to use
  const getBestFamily = async (familyMemberships: any[]) => {
    // Check localStorage for saved family
    const savedFamilyId = localStorage.getItem('currentFamilyId');
    const savedFamily = familyMemberships.find(fm => fm.families?.id === savedFamilyId);
    
    if (savedFamily?.families) {
      return savedFamily.families;
    }

    // If no saved family or saved family not found, pick the one with most activity
    let bestFamily = null;
    let bestScore = -1;

    for (const membership of familyMemberships) {
      if (!membership.families) continue;
      
      const familyId = membership.families.id;
      
      // Count events for this family
      const { count: eventsCount } = await supabase
        .from('family_events')
        .select('*', { count: 'exact', head: true })
        .eq('family_id', familyId);

      // Count tasks for this family
      const { count: tasksCount } = await supabase
        .from('family_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('family_id', familyId);

      // Count person_profiles for this family
      const { count: membersCount } = await supabase
        .from('person_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('family_id', familyId);

      const score = (eventsCount || 0) + (tasksCount || 0) + (membersCount || 0);
      
      if (score > bestScore) {
        bestScore = score;
        bestFamily = membership.families;
      }
    }

    // Save the selected family to localStorage
    if (bestFamily) {
      localStorage.setItem('currentFamilyId', bestFamily.id);
    }

    return bestFamily || familyMemberships[0]?.families;
  };

  // Combined query for profile, family and members with intelligent family selection
  const familyDataQuery = useQuery({
    queryKey: ['family-data', user?.id],
    queryFn: async () => {
      if (!user) return { profile: null, family: null, familyMembers: [], families: [] };

      // Fetch user profile - exact same logic as useFamily.tsx
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;

      // Fetch family data using new family_memberships approach
      const { data: familyMemberships, error: membershipError } = await supabase
        .from('family_memberships')
        .select(`
          role,
          joined_at,
          families:family_id (
            id,
            name,
            owner_id,
            created_by,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id);

      if (membershipError) {
        console.error('Error fetching family memberships:', membershipError);
        return { profile: profileData, family: null, familyMembers: [], families: [] };
      }

      if (!familyMemberships || familyMemberships.length === 0) {
        return { profile: profileData, family: null, familyMembers: [], families: [] };
      }

      // Use intelligent family selection
      const familyData = await getBestFamily(familyMemberships);

      if (!familyData) {
        return { profile: profileData, family: null, familyMembers: [] };
      }

      // Fetch all family members (from person_profiles for better data)
      const { data: personProfiles, error: personProfilesError } = await supabase
        .from('person_profiles')
        .select('*')
        .eq('family_id', familyData.id);

      if (personProfilesError) {
        console.error('Error fetching person profiles:', personProfilesError);
        return { profile: profileData, family: familyData, familyMembers: [] };
      }

      // Fetch profiles for members who have profile_id
      const profileIds = personProfiles
        .filter(p => p.profile_id)
        .map(p => p.profile_id);

      let profiles = [];
      if (profileIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, avatar_url')
          .in('id', profileIds);

        if (!profilesError && profilesData) {
          profiles = profilesData;
        }
      }

      // Convert person_profiles to FamilyMember format (filtered for direct family only)
      const directFamilyRoles = ['father', 'mother', 'parent', 'spouse', 'son', 'daughter', 'child'];
      
      const filteredPersonProfiles = personProfiles
        .filter(p => p.category === 'family' && directFamilyRoles.includes(p.family_role));
      
      const membersWithProfiles = filteredPersonProfiles.map(person => ({
        id: person.id,
        family_id: person.family_id,
        role: person.family_role || 'child' as any,
        is_creator: person.created_by === profileData.id,
        child_name: `${person.first_name} ${person.last_name}`,
        child_birth_date: person.date_of_birth,
        child_gender: person.gender,
        child_avatar_url: person.avatar_url,
        profile_id: person.profile_id,
        created_at: person.created_at,
        updated_at: person.updated_at,
        profiles: person.profile_id ? profiles.find(p => p.id === person.profile_id) || null : null
      }));

      // Remove duplicates based on profile_id or full name for those without profile_id
      const uniqueMembers = membersWithProfiles.reduce((acc, member) => {
        const existingIndex = acc.findIndex(existing => 
          (member.profile_id && existing.profile_id && member.profile_id === existing.profile_id) ||
          (!member.profile_id && !existing.profile_id && member.child_name === existing.child_name)
        );
        
        if (existingIndex === -1) {
          acc.push(member);
        } else {
          // Keep the one with more complete data (has profile_id or avatar)
          if (member.profile_id && !acc[existingIndex].profile_id) {
            acc[existingIndex] = member;
          } else if (member.child_avatar_url && !acc[existingIndex].child_avatar_url) {
            acc[existingIndex] = member;
          }
        }
        return acc;
      }, [] as typeof membersWithProfiles);

      return {
        profile: profileData,
        family: familyData,
        familyMembers: uniqueMembers,
        families: familyMemberships.map(fm => ({
          family: fm.families,
          role: fm.role,
          joined_at: fm.joined_at
        }))
      };
    },
    enabled: !!user,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    refetchOnWindowFocus: false,
  });

  // Mutation to create family - exact same logic as useFamily.tsx
  const createFamilyMutation = useMutation({
    mutationFn: async (familyName?: string) => {
      if (!familyDataQuery.data?.profile) throw new Error('Profile not found');

      // Use RPC function for creating family with membership
      const { data: familyData, error: familyError } = await supabase
        .rpc('create_family_with_membership', {
          family_name: familyName || 'Ma Famille'
        });

      if (familyError) throw familyError;

      return familyData;
    },
    onSuccess: async (data, familyName) => {
      const newFamilyId = (data as any)?.id;
      const newFamilyName = (data as any)?.name || familyName || 'Ma Famille';
      
      console.log(`🎉 New family created with ID: ${newFamilyId}`);
      
      // FIRST: Clear all family-related caches to prevent data contamination
      clearAllFamilyCache();
      
      // THEN: Switch to the new family
      if (newFamilyId) {
        localStorage.setItem('currentFamilyId', newFamilyId);
        console.log(`💾 Switched to new family: ${newFamilyId}`);
      }
      
      // FINALLY: Refetch family data for the new family
      queryClient.invalidateQueries({ queryKey: ['family-data', user?.id] });
      
      // Redirect to onboarding instead of showing success toast
      if (newFamilyId) {
        const { redirectBasedOnOnboardingStatus } = await import('@/utils/onboardingUtils');
        await redirectBasedOnOnboardingStatus(newFamilyId, newFamilyName);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Prefetch related data when family is available
  const prefetchFamilyData = () => {
    if (familyDataQuery.data?.family?.id) {
      // Prefetch events
      queryClient.prefetchQuery({
        queryKey: ['events', familyDataQuery.data.family.id],
        staleTime: STALE_TIME,
      });
      
      // Prefetch tasks
      queryClient.prefetchQuery({
        queryKey: ['tasks', familyDataQuery.data.family.id],
        staleTime: STALE_TIME,
      });
    }
  };

  // Function to clear all family-related caches
  const clearAllFamilyCache = () => {
    console.log('🧹 Clearing all family-related caches...');
    queryClient.invalidateQueries();
    console.log('✅ All family caches cleared');
  };

  // Function to switch family
  const switchFamily = (familyId: string) => {
    console.log(`🔄 Switching to family: ${familyId}`);
    
    // First clear all caches to prevent data contamination
    clearAllFamilyCache();
    
    // Then update localStorage
    localStorage.setItem('currentFamilyId', familyId);
    
    // Finally refetch current family data
    queryClient.invalidateQueries({ queryKey: ['family-data', user?.id] });
  };

  // Update family mutation
  const updateFamilyMutation = useMutation({
    mutationFn: async ({ familyId, name }: { familyId: string; name: string }) => {
      const { error } = await supabase
        .from('families')
        .update({ name: name.trim() })
        .eq('id', familyId);

      if (error) throw error;
      return { familyId, name };
    },
    onSuccess: (data) => {
      toast({
        title: "Nom mis à jour",
        description: `Le nom de votre clan a été changé pour "${data.name}".`,
      });
      queryClient.invalidateQueries({ queryKey: ['family-data', user?.id] });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete family mutation
  const deleteFamilyMutation = useMutation({
    mutationFn: async (familyId: string) => {
      const { error } = await supabase
        .from('families')
        .delete()
        .eq('id', familyId);

      if (error) throw error;
      return familyId;
    },
    onSuccess: (deletedFamilyId) => {
      toast({
        title: "Clan supprimé",
        description: "Le clan a été supprimé avec succès.",
      });

      // If we deleted the current family, switch to another one
      if (familyDataQuery.data?.family?.id === deletedFamilyId) {
        localStorage.removeItem('currentFamilyId');
        
        const remainingFamilies = familyDataQuery.data?.families?.filter(f => f.family?.id !== deletedFamilyId) || [];
        if (remainingFamilies.length > 0) {
          const nextFamily = remainingFamilies[0];
          localStorage.setItem('currentFamilyId', nextFamily.family.id);
          
          // Clear caches and refresh data
          clearAllFamilyCache();
          queryClient.invalidateQueries({ queryKey: ['family-data', user?.id] });
          if (window.location.pathname !== '/dashboard') {
            window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path: '/dashboard', replace: true } }));
          }
        } else {
          // No families left, redirect to onboarding to create a new one
          if (window.location.pathname !== '/new-user-onboarding') {
            window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path: '/new-user-onboarding', replace: true } }));
          }
        }
      } else {
        // Just refresh the family list
        queryClient.invalidateQueries({ queryKey: ['family-data', user?.id] });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer le clan.",
        variant: "destructive",
      });
    },
  });

  return {
    profile: familyDataQuery.data?.profile || null,
    family: familyDataQuery.data?.family || null,
    familyMembers: familyDataQuery.data?.familyMembers || [],
    families: familyDataQuery.data?.families || [],
    currentFamily: familyDataQuery.data?.families?.find(f => f.family?.id === familyDataQuery.data?.family?.id),
    loading: familyDataQuery.isLoading,
    isError: familyDataQuery.isError,
    error: familyDataQuery.error,
    createFamily: createFamilyMutation.mutate,
    isCreating: createFamilyMutation.isPending,
    switchFamily,
    updateFamily: updateFamilyMutation.mutate,
    isUpdating: updateFamilyMutation.isPending,
    deleteFamily: deleteFamilyMutation.mutate,
    isDeleting: deleteFamilyMutation.isPending,
    prefetchFamilyData,
    refreshData: () => familyDataQuery.refetch(),
  };
}